import React from "react";
import {
  Sparkles,
  Replace,
  Boxes,
  TrendingUp,
  LineChart,
  Brain,
  ShieldCheck,
} from "lucide-react";
import { useLocale } from "../../i18n/LocaleContext";

const CAPABILITIES = [
  {
    icon: Sparkles,
    title: "Intelligent Product Matching",
    body:
      "Topaz AI parses unstructured buyer queries — free-text, part numbers, photos, descriptions — and matches them across the full 30M+ SKU catalog of OEM, manufacturer and distributor records in milliseconds.",
    metric: "98% match accuracy",
  },
  {
    icon: Replace,
    title: "Alternate Product Recommendation",
    body:
      "When the requested item is end-of-life, out of stock or off-contract, Topaz surfaces functionally-equivalent alternates with stock, lead time and price delta side-by-side.",
    metric: "Avg. 11% unit-price savings",
  },
  {
    icon: Boxes,
    title: "SKU & Item-Master Standardization",
    body:
      "Deduplicates and harmonizes legacy material masters across plants, BUs and geographies — clean, classified, taxonomy-aligned item data ready for analytics.",
    metric: "60–80% duplicate reduction",
  },
  {
    icon: TrendingUp,
    title: "Spend Optimization",
    body:
      "Continuously analyzes patterns across plants and categories — flags maverick spend, recommends bundling, contract switches and volume tier upgrades.",
    metric: "12–18% addressable leakage",
  },
  {
    icon: LineChart,
    title: "Demand Forecasting & Stock Signals",
    body:
      "Anticipates plant-level consumption from historical PO and GRN data; pre-positions inventory commitments with OEM partners ahead of demand.",
    metric: "Up to 24-week visibility",
  },
  {
    icon: ShieldCheck,
    title: "Responsible AI by Design",
    body:
      "Built on Infosys Responsible AI — full auditability, explainable matches, supplier ESG and sanctions screening baked into every recommendation.",
    metric: "Audit-grade governance",
  },
];

export default function TopazAI() {
  const { t } = useLocale();
  return (
    <section
      id="topaz"
      data-testid="topaz-section"
      className="relative bg-white border-b border-slate-200 py-24 md:py-32"
    >
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-slate-200 to-transparent" />

      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        {/* Section header */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-16 items-end">
          <div className="lg:col-span-7">
            <div className="flex items-center gap-3 text-[11px] uppercase tracking-[0.22em] text-slate-500 font-semibold mb-4">
              <span className="inline-block h-px w-8 bg-slate-300" />
              <span>{t("landing.topaz.eyebrow")}</span>
            </div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl xl:text-[56px] font-semibold tracking-[-0.02em] leading-[1.05] text-slate-900">
              {t("landing.topaz.headline_a")}{" "}
              <span className="relative inline-block">
                <span className="text-[#0072CE]">{t("landing.topaz.headline_b")}</span>
                <sup className="text-xs text-slate-400 ml-0.5">™</sup>
              </span>{" "}
              {t("landing.topaz.headline_c")}
            </h2>
          </div>
          <div className="lg:col-span-5">
            <p className="text-slate-600 text-lg leading-relaxed">
              {t("landing.topaz.body")}
            </p>
            <div className="mt-5 inline-flex items-center gap-2 text-sm text-slate-700 border border-slate-200 rounded-full px-4 py-1.5">
              <Brain size={14} className="text-[#0072CE]" />
              <span className="font-medium">
                Frontier LLMs · Specialized SLMs · Responsible AI
              </span>
            </div>
          </div>
        </div>

        {/* Capabilities grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-slate-200 border border-slate-200 rounded-2xl overflow-hidden">
          {CAPABILITIES.map((c, i) => (
            <article
              key={c.title}
              data-testid={`topaz-capability-${i}`}
              className="group bg-white p-7 md:p-8 hover:bg-slate-50 transition-colors relative flex flex-col"
            >
              <div className="flex items-start justify-between">
                <div className="h-11 w-11 rounded-md border border-slate-200 bg-slate-50 text-[#0072CE] flex items-center justify-center group-hover:bg-[#0072CE] group-hover:text-white transition-colors">
                  <c.icon size={20} />
                </div>
                <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-400">
                  0{i + 1}
                </span>
              </div>
              <h3 className="font-display text-xl font-semibold tracking-tight text-slate-900 mt-6">
                {c.title}
              </h3>
              <p className="mt-2.5 text-slate-600 leading-relaxed text-sm flex-1">
                {c.body}
              </p>
              <div className="mt-6 pt-5 border-t border-slate-200">
                <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-400">
                  Outcome
                </div>
                <div className="mt-1 font-display text-base font-semibold text-slate-900">
                  {c.metric}
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
